import React, { useState, useMemo } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from './Toast';
import { useConfirm } from './ConfirmModal';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

// Managers and rubber-tier users that can be assigned as regional managers
function getEligibleManagers(salesTeam) {
  return salesTeam.filter(u => u.salesRole === 'manager' || u.tier === 'rubber');
}

export default function MapAdmin({ regions, onRegionsChange, markers, salesTeam }) {
  const { token } = useAuth();
  const showToast = useToast();
  const confirm = useConfirm();

  const [tab, setTab] = useState('regions');

  // --- Regions tab state ---
  const [creating, setCreating] = useState(false);
  const [newRegionName, setNewRegionName] = useState('');
  const [newRegionManager, setNewRegionManager] = useState('');
  const [editingRegionId, setEditingRegionId] = useState(null);
  const [editName, setEditName] = useState('');
  const [editManagerId, setEditManagerId] = useState('');
  const [saving, setSaving] = useState(false);

  // --- Client assignment tab state ---
  const [clientSearch, setClientSearch] = useState('');
  const [filterRegion, setFilterRegion] = useState('');
  const [assigningId, setAssigningId] = useState(null);

  // --- Digest tab state ---
  const [digestLoading, setDigestLoading] = useState(false);
  const [digestResult, setDigestResult] = useState(null);

  const eligibleManagers = useMemo(() => getEligibleManagers(salesTeam), [salesTeam]);


  // ---- Regions CRUD ----

  const handleCreateRegion = async () => {
    if (!newRegionName.trim()) return;
    setSaving(true);
    try {
      const res = await axios.post(
        `${API_URL}/api/regions`,
        { name: newRegionName.trim(), managerId: newRegionManager || null },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      onRegionsChange([...regions, res.data]);
      setNewRegionName('');
      setNewRegionManager('');
      setCreating(false);
      showToast(`Region "${res.data.name}" created`, 'success');
    } catch (err) {
      showToast(err.response?.data?.error || 'Failed to create region', 'error');
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (region) => {
    setEditingRegionId(region._id);
    setEditName(region.name);
    setEditManagerId(region.manager || '');
  };

  const handleSaveEdit = async () => {
    setSaving(true);
    try {
      const res = await axios.put(
        `${API_URL}/api/regions/${editingRegionId}`,
        { name: editName.trim(), managerId: editManagerId || null },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      onRegionsChange(regions.map(r => r._id === editingRegionId ? { ...res.data, clientCount: r.clientCount } : r));
      setEditingRegionId(null);
      showToast('Region updated', 'success');
    } catch (err) {
      showToast(err.response?.data?.error || 'Failed to update region', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteRegion = async (region) => {
    const yes = await confirm(`Delete region "${region.name}"? All clients in this region will be unassigned.`);
    if (!yes) return;
    try {
      await axios.delete(
        `${API_URL}/api/regions/${region._id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      onRegionsChange(regions.filter(r => r._id !== region._id));
      showToast(`Region "${region.name}" deleted`, 'success');
    } catch (err) {
      showToast(err.response?.data?.error || 'Failed to delete region', 'error');
    }
  };

  // ---- Client region assignment ----

  const filteredClients = useMemo(() => {
    let list = markers.filter(m => !m.archived);
    if (clientSearch.trim()) {
      const q = clientSearch.trim().toLowerCase();
      list = list.filter(m => m.clientName.toLowerCase().includes(q));
    }
    if (filterRegion === '__none__') {
      list = list.filter(m => !m.region);
    } else if (filterRegion) {
      list = list.filter(m => m.region === filterRegion || m.region?.toString() === filterRegion);
    }
    return list.sort((a, b) => a.clientName.localeCompare(b.clientName));
  }, [markers, clientSearch, filterRegion]);

  const handleAssignRegion = async (markerId, regionId) => {
    setAssigningId(markerId);
    try {
      await axios.patch(
        `${API_URL}/api/client-markers/${markerId}/region`,
        { regionId: regionId || null },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const regionName = regionId ? (regions.find(r => r._id === regionId)?.name || '') : null;
      // Update client count on regions
      const oldClient = markers.find(m => m._id === markerId);
      const newRegions = regions.map(r => {
        let count = r.clientCount || 0;
        if (oldClient?.region?.toString() === r._id.toString()) count = Math.max(0, count - 1);
        if (regionId && r._id === regionId) count += 1;
        return { ...r, clientCount: count };
      });
      onRegionsChange(newRegions);
      // We notify the parent to re-fetch markers so the region field is current
      showToast(`Region ${regionName ? `set to "${regionName}"` : 'cleared'}`, 'success');
    } catch (err) {
      showToast(err.response?.data?.error || 'Failed to assign region', 'error');
    } finally {
      setAssigningId(null);
    }
  };

  // ---- Digest ----

  const handleSendDigest = async () => {
    const yes = await confirm(
      'Send the stale leads digest email now?\n\nEach rep with leads inactive for 30+ days will receive an email. Their regional manager will be CC\'d.'
    );
    if (!yes) return;
    setDigestLoading(true);
    setDigestResult(null);
    try {
      const res = await axios.post(
        `${API_URL}/api/regions/send-stale-digest`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setDigestResult(res.data);
      if (res.data.error) {
        showToast(res.data.error, 'error');
      } else if (res.data.sent === 0) {
        showToast(res.data.message || 'No stale leads found — no emails sent.', 'info');
      } else {
        showToast(`Digest sent to ${res.data.sent} rep${res.data.sent !== 1 ? 's' : ''}`, 'success');
      }
    } catch (err) {
      showToast(err.response?.data?.error || 'Failed to send digest', 'error');
    } finally {
      setDigestLoading(false);
    }
  };

  // ---- Render ----

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h2 style={styles.title}>Maps Admin</h2>
        <p style={styles.subtitle}>Manage sales regions, assign regional managers, and send stale lead alerts.</p>
      </div>

      {/* Inner tabs */}
      <div style={styles.innerTabs}>
        {[
          { key: 'regions', label: 'Regions' },
          { key: 'clients', label: 'Client Assignments' },
          { key: 'digest', label: 'Email Digest' }
        ].map(t => (
          <button
            key={t.key}
            style={{ ...styles.innerTab, ...(tab === t.key ? styles.innerTabActive : {}) }}
            onClick={() => setTab(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* =================== REGIONS TAB =================== */}
      {tab === 'regions' && (
        <div>
          <div style={styles.sectionHeader}>
            <span style={styles.sectionTitle}>Regions ({regions.length})</span>
            {!creating && (
              <button style={styles.primaryBtn} onClick={() => setCreating(true)}>
                + New Region
              </button>
            )}
          </div>

          {/* Create form */}
          {creating && (
            <div style={styles.createCard}>
              <div style={styles.formRow}>
                <input
                  style={styles.input}
                  placeholder="Region name (e.g. Gauteng North)"
                  value={newRegionName}
                  onChange={e => setNewRegionName(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleCreateRegion()}
                  autoFocus
                />
                <select
                  style={styles.select}
                  value={newRegionManager}
                  onChange={e => setNewRegionManager(e.target.value)}
                >
                  <option value="">— No manager —</option>
                  {eligibleManagers.map(m => (
                    <option key={m._id} value={m._id}>{m.name}</option>
                  ))}
                </select>
              </div>
              <div style={styles.formActions}>
                <button style={styles.primaryBtn} onClick={handleCreateRegion} disabled={saving || !newRegionName.trim()}>
                  {saving ? 'Creating…' : 'Create Region'}
                </button>
                <button style={styles.ghostBtn} onClick={() => { setCreating(false); setNewRegionName(''); setNewRegionManager(''); }}>
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* Regions list */}
          {regions.length === 0 && !creating ? (
            <div style={styles.empty}>No regions yet. Create one to get started.</div>
          ) : (
            <div style={styles.regionList}>
              {regions.map(region => (
                <div key={region._id} style={styles.regionCard}>
                  {editingRegionId === region._id ? (
                    /* Inline edit */
                    <div style={styles.editRow}>
                      <input
                        style={{ ...styles.input, flex: 1 }}
                        value={editName}
                        onChange={e => setEditName(e.target.value)}
                        autoFocus
                      />
                      <select
                        style={styles.select}
                        value={editManagerId}
                        onChange={e => setEditManagerId(e.target.value)}
                      >
                        <option value="">— No manager —</option>
                        {eligibleManagers.map(m => (
                          <option key={m._id} value={m._id}>{m.name}</option>
                        ))}
                      </select>
                      <button style={styles.primaryBtn} onClick={handleSaveEdit} disabled={saving}>
                        {saving ? '…' : 'Save'}
                      </button>
                      <button style={styles.ghostBtn} onClick={() => setEditingRegionId(null)}>Cancel</button>
                    </div>
                  ) : (
                    <div style={styles.regionRow}>
                      <div style={styles.regionInfo}>
                        <span style={styles.regionName}>{region.name}</span>
                        <span style={styles.regionMeta}>
                          {region.managerName
                            ? <span style={styles.managerBadge}>👤 {region.managerName}</span>
                            : <span style={styles.noManager}>No manager assigned</span>
                          }
                          <span style={styles.clientCount}>{region.clientCount || 0} client{(region.clientCount || 0) !== 1 ? 's' : ''}</span>
                        </span>
                      </div>
                      <div style={styles.regionActions}>
                        <button style={styles.editBtn} onClick={() => startEdit(region)}>Edit</button>
                        <button style={styles.deleteBtn} onClick={() => handleDeleteRegion(region)}>Delete</button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* =================== CLIENT ASSIGNMENTS TAB =================== */}
      {tab === 'clients' && (
        <div>
          <div style={styles.sectionHeader}>
            <span style={styles.sectionTitle}>Assign Clients to Regions</span>
          </div>

          <div style={styles.filterRow}>
            <input
              style={{ ...styles.input, flex: 1 }}
              placeholder="Search client name…"
              value={clientSearch}
              onChange={e => setClientSearch(e.target.value)}
            />
            <select
              style={styles.select}
              value={filterRegion}
              onChange={e => setFilterRegion(e.target.value)}
            >
              <option value="">All regions</option>
              <option value="__none__">No region assigned</option>
              {regions.map(r => (
                <option key={r._id} value={r._id}>{r.name}</option>
              ))}
            </select>
          </div>

          <div style={styles.clientCount2}>
            Showing {filteredClients.length} client{filteredClients.length !== 1 ? 's' : ''}
          </div>

          <div style={styles.clientTable}>
            <div style={styles.tableHead}>
              <span style={{ flex: 2 }}>Client</span>
              <span style={{ flex: 1 }}>Assigned Rep</span>
              <span style={{ flex: 1.5 }}>Region</span>
            </div>
            {filteredClients.length === 0 && (
              <div style={styles.empty}>No clients match the current filter.</div>
            )}
            {filteredClients.map(client => (
              <div key={client._id} style={styles.tableRow}>
                <span style={{ flex: 2, fontWeight: 600, color: '#e8eaf0' }}>{client.clientName}</span>
                <span style={{ flex: 1, color: '#aaa', fontSize: 13 }}>
                  {client.claimedByName || <em style={{ color: '#666' }}>Unassigned</em>}
                </span>
                <span style={{ flex: 1.5 }}>
                  <select
                    style={{ ...styles.select, width: '100%', opacity: assigningId === client._id ? 0.6 : 1 }}
                    value={client.region?.toString() || ''}
                    disabled={assigningId === client._id}
                    onChange={e => handleAssignRegion(client._id, e.target.value || null)}
                  >
                    <option value="">— No region —</option>
                    {regions.map(r => (
                      <option key={r._id} value={r._id}>{r.name}</option>
                    ))}
                  </select>
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* =================== DIGEST TAB =================== */}
      {tab === 'digest' && (
        <div>
          <div style={styles.sectionHeader}>
            <span style={styles.sectionTitle}>Stale Leads Email Digest</span>
          </div>

          <div style={styles.digestCard}>
            <div style={styles.digestIcon}>📧</div>
            <h3 style={styles.digestTitle}>Weekly Stale Leads Reminder</h3>
            <p style={styles.digestDesc}>
              Every <strong>Monday at 08:00</strong> the system automatically emails each sales rep
              a list of their leads with <strong>no activity for 30 or more days</strong>.
              Their regional manager is <strong>CC'd</strong> so management can track performance.
            </p>
            <p style={styles.digestDesc}>
              Use the button below to send the digest <em>immediately</em> — useful for testing
              or triggering an out-of-cycle alert.
            </p>

            <button
              style={{ ...styles.primaryBtn, fontSize: 15, padding: '12px 28px', marginTop: 8 }}
              onClick={handleSendDigest}
              disabled={digestLoading}
            >
              {digestLoading ? 'Sending…' : '📧 Send Stale Leads Digest Now'}
            </button>

            {digestResult && (
              <div style={styles.digestResult}>
                {digestResult.error ? (
                  <div style={{ color: '#e74c3c' }}>⚠️ {digestResult.error}</div>
                ) : digestResult.total === 0 ? (
                  <div style={{ color: '#4caf50' }}>✅ No stale leads found — no emails sent.</div>
                ) : (
                  <>
                    <div style={styles.digestStatRow}>
                      <span style={styles.digestStat}>{digestResult.total}</span>
                      <span style={styles.digestStatLabel}>stale leads found</span>
                    </div>
                    <div style={styles.digestStatRow}>
                      <span style={styles.digestStat}>{digestResult.sent}</span>
                      <span style={styles.digestStatLabel}>reps notified</span>
                    </div>
                    {digestResult.skipped > 0 && (
                      <div style={styles.digestStatRow}>
                        <span style={{ ...styles.digestStat, color: '#e74c3c' }}>{digestResult.skipped}</span>
                        <span style={styles.digestStatLabel}>skipped (no email on file)</span>
                      </div>
                    )}
                    {digestResult.results && digestResult.results.length > 0 && (
                      <div style={styles.digestDetails}>
                        {digestResult.results.map((r, i) => (
                          <div key={i} style={styles.digestDetailRow}>
                            <span style={{ fontWeight: 600 }}>{r.rep || r.repId}</span>
                            <span style={{ color: '#aaa', marginLeft: 8 }}>{r.leads} lead{r.leads !== 1 ? 's' : ''}</span>
                            {r.cc && r.cc.length > 0 && (
                              <span style={{ color: '#888', marginLeft: 8, fontSize: 12 }}>CC: {r.cc.join(', ')}</span>
                            )}
                            <span style={{
                              marginLeft: 'auto', fontSize: 12,
                              color: r.status === 'sent' ? '#4caf50' : '#e74c3c'
                            }}>
                              {r.status === 'sent' ? '✓ sent' : r.status === 'skipped' ? `⚠ skipped — ${r.reason}` : '✗ failed'}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </div>

          <div style={styles.infoBox}>
            <strong>Note:</strong> For a rep to receive the digest, they must have an email address
            on their account and an assigned sales role. For the regional manager to be CC'd, the client must be assigned to a region
            with a manager. Leads must have status "New Lead" and their last activity date must be more
            than 30 days ago.
          </div>
        </div>
      )}

    </div>
  );
}

const styles = {
  container: { padding: '24px', maxWidth: 920, margin: '0 auto' },
  header: { marginBottom: 20 },
  title: { margin: '0 0 4px', fontSize: 22, fontWeight: 700, color: '#e8eaf0' },
  subtitle: { margin: 0, fontSize: 14, color: '#888' },

  innerTabs: { display: 'flex', gap: 6, marginBottom: 24, borderBottom: '1px solid #333', paddingBottom: 0 },
  innerTab: {
    padding: '9px 20px', border: 'none', background: 'transparent', color: '#888',
    fontSize: 14, fontWeight: 600, cursor: 'pointer', borderRadius: '8px 8px 0 0',
    borderBottom: '2px solid transparent', transition: 'all 0.15s'
  },
  innerTabActive: {
    color: '#5dade2', borderBottom: '2px solid #5dade2', background: 'rgba(93,173,226,0.07)'
  },

  sectionHeader: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    marginBottom: 16
  },
  sectionTitle: { fontSize: 16, fontWeight: 700, color: '#ccc' },

  createCard: {
    background: 'rgba(255,255,255,0.05)', border: '1px solid #333',
    borderRadius: 10, padding: 16, marginBottom: 16
  },
  formRow: { display: 'flex', gap: 10, marginBottom: 10, flexWrap: 'wrap' },
  formActions: { display: 'flex', gap: 8 },

  input: {
    flex: 1, minWidth: 180, padding: '9px 12px', background: '#1e1e2e',
    border: '1px solid #444', borderRadius: 8, color: '#e8eaf0',
    fontSize: 14, fontFamily: 'inherit', outline: 'none'
  },
  select: {
    flex: 1, minWidth: 160, padding: '9px 12px', background: '#1e1e2e',
    border: '1px solid #444', borderRadius: 8, color: '#e8eaf0',
    fontSize: 14, fontFamily: 'inherit', cursor: 'pointer'
  },

  primaryBtn: {
    padding: '9px 18px', background: '#5dade2', color: '#fff',
    border: 'none', borderRadius: 8, fontWeight: 700, fontSize: 13,
    cursor: 'pointer', whiteSpace: 'nowrap'
  },
  ghostBtn: {
    padding: '9px 16px', background: 'transparent', color: '#aaa',
    border: '1px solid #444', borderRadius: 8, fontWeight: 600, fontSize: 13, cursor: 'pointer'
  },
  editBtn: {
    padding: '6px 14px', background: 'rgba(93,173,226,0.15)', color: '#5dade2',
    border: '1px solid rgba(93,173,226,0.3)', borderRadius: 7, fontSize: 13,
    fontWeight: 600, cursor: 'pointer'
  },
  deleteBtn: {
    padding: '6px 14px', background: 'rgba(231,76,60,0.12)', color: '#e74c3c',
    border: '1px solid rgba(231,76,60,0.25)', borderRadius: 7, fontSize: 13,
    fontWeight: 600, cursor: 'pointer'
  },

  regionList: { display: 'flex', flexDirection: 'column', gap: 8 },
  regionCard: {
    background: 'rgba(255,255,255,0.04)', border: '1px solid #333',
    borderRadius: 10, padding: '14px 16px'
  },
  regionRow: { display: 'flex', alignItems: 'center', gap: 12 },
  editRow: { display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' },
  regionInfo: { flex: 1, display: 'flex', flexDirection: 'column', gap: 4 },
  regionName: { fontSize: 15, fontWeight: 700, color: '#e8eaf0' },
  regionMeta: { display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' },
  managerBadge: { fontSize: 13, color: '#5dade2', fontWeight: 600 },
  noManager: { fontSize: 13, color: '#666', fontStyle: 'italic' },
  clientCount: { fontSize: 12, color: '#888', background: 'rgba(255,255,255,0.06)', padding: '2px 8px', borderRadius: 20 },
  regionActions: { display: 'flex', gap: 6 },

  // Client assignments
  filterRow: { display: 'flex', gap: 10, marginBottom: 12, flexWrap: 'wrap' },
  clientCount2: { fontSize: 12, color: '#666', marginBottom: 8 },
  clientTable: {
    border: '1px solid #333', borderRadius: 10, overflow: 'hidden'
  },
  tableHead: {
    display: 'flex', gap: 12, padding: '10px 16px',
    background: 'rgba(255,255,255,0.04)', fontSize: 11, fontWeight: 700,
    color: '#888', textTransform: 'uppercase', letterSpacing: 0.5,
    borderBottom: '1px solid #333'
  },
  tableRow: {
    display: 'flex', gap: 12, padding: '11px 16px', alignItems: 'center',
    borderBottom: '1px solid #2a2a2a', fontSize: 14
  },

  empty: { color: '#666', fontStyle: 'italic', padding: '20px 0', textAlign: 'center' },

  // Digest
  digestCard: {
    background: 'rgba(255,255,255,0.04)', border: '1px solid #333',
    borderRadius: 12, padding: '28px 32px', marginBottom: 20, maxWidth: 600
  },
  digestIcon: { fontSize: 36, marginBottom: 12 },
  digestTitle: { margin: '0 0 12px', fontSize: 18, fontWeight: 700, color: '#e8eaf0' },
  digestDesc: { margin: '0 0 10px', fontSize: 14, color: '#aaa', lineHeight: 1.6 },

  digestResult: {
    marginTop: 20, padding: '16px', background: 'rgba(0,0,0,0.25)',
    borderRadius: 8, border: '1px solid #333'
  },
  digestStatRow: { display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 6 },
  digestStat: { fontSize: 26, fontWeight: 800, color: '#5dade2' },
  digestStatLabel: { fontSize: 14, color: '#aaa' },
  digestDetails: { marginTop: 12, display: 'flex', flexDirection: 'column', gap: 6 },
  digestDetailRow: {
    display: 'flex', alignItems: 'center', fontSize: 13, color: '#ccc',
    padding: '6px 0', borderTop: '1px solid #2a2a2a'
  },

  infoBox: {
    background: 'rgba(93,173,226,0.08)', border: '1px solid rgba(93,173,226,0.2)',
    borderRadius: 8, padding: '14px 16px', fontSize: 13, color: '#aaa', lineHeight: 1.6
  },

};
